/**
 * UnmatchedInstructorRequestsCard — calm operational signal on the
 * Admin command centre (AdminHub).
 *
 * May 14, 2026 — counts pupils who tapped "Find me an instructor"
 * but still have no instructor linked after the 7-day grace window.
 * Mirrors the eligibility rule used by the pupil-side
 * StillLookingInstructorBanner so the count is consistent.
 *
 * Behaviour:
 *   • Fetches /api/admin/unmatched-instructor-requests on mount.
 *   • Renders only when count > 0 (silent otherwise — no noise).
 *   • Clicking the card opens the filtered list page.
 *   • Admin-only — the backend gates the endpoint with get_admin_user.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Compass, ChevronRight, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function UnmatchedInstructorRequestsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await axios.get(
          `${API}/api/admin/unmatched-instructor-requests`,
          { withCredentials: true },
        );
        if (alive) setData(d);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Keep the card silent when there's nothing to action — admins
  // shouldn't see zeros for low-volume signals.
  if (loading) {
    return (
      <div
        data-testid="admin-unmatched-instructor-loading"
        className="flex items-center gap-2 text-xs text-slate-400 px-2"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading operational signals…
      </div>
    );
  }
  if (!data || data.count === 0) return null;

  return (
    <Link
      to="/admin/unmatched-instructor-requests"
      data-testid="admin-unmatched-instructor-card"
      data-count={data.count}
      className="group flex items-center gap-3 rounded-2xl bg-white border border-amber-300 p-4 shadow-sm hover:border-amber-400 hover:shadow-md active:scale-[0.99] transition-all"
    >
      <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
        <Compass className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span
            data-testid="admin-unmatched-instructor-count"
            className="text-2xl font-black text-slate-900 leading-none"
          >
            {data.count}
          </span>
          <p className="text-sm font-extrabold text-slate-900 leading-tight">
            Unmatched instructor requests
          </p>
        </div>
        <p className="text-xs text-slate-500 mt-1 leading-snug">
          Pupils who asked to find an instructor but have not matched after
          {" "}{data.grace_days ?? 7} days.
        </p>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-amber-500 flex-shrink-0" />
    </Link>
  );
}
